import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createVaultEntry,
  deleteVaultEntry,
  listVaultAudit,
  listVaultEntries,
  updateVaultEntry,
  type VaultEntryInput,
} from '../lib/vaultApi';

// Credentials vault — react-query wrappers. Both the entry list and the audit
// log live under ['vault', projectId] so a single invalidate refreshes them
// together after any write (every write also appends an audit row).
function vaultKey(projectId: string) {
  return ['vault', projectId] as const;
}

export function useVaultEntries(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...vaultKey(projectId ?? ''), 'entries'],
    queryFn: () => listVaultEntries(projectId as string),
    enabled: Boolean(projectId) && enabled,
  });
}

export function useVaultAudit(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...vaultKey(projectId ?? ''), 'audit'],
    queryFn: () => listVaultAudit(projectId as string),
    enabled: Boolean(projectId) && enabled,
  });
}

function useInvalidateVault(projectId: string) {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: vaultKey(projectId) });
}

export function useCreateVaultEntry(projectId: string) {
  const invalidate = useInvalidateVault(projectId);
  return useMutation({
    mutationFn: (input: VaultEntryInput) => createVaultEntry(projectId, input),
    onSuccess: invalidate,
  });
}

export function useUpdateVaultEntry(projectId: string) {
  const invalidate = useInvalidateVault(projectId);
  return useMutation({
    mutationFn: ({ entryId, input }: { entryId: string; input: VaultEntryInput }) =>
      updateVaultEntry(projectId, entryId, input),
    onSuccess: invalidate,
  });
}

export function useDeleteVaultEntry(projectId: string) {
  const invalidate = useInvalidateVault(projectId);
  return useMutation({
    mutationFn: (entryId: string) => deleteVaultEntry(projectId, entryId),
    onSuccess: invalidate,
  });
}
